/**
 * =============================================================================
 * Componente: BrandingPreview
 * Versão: 1.0.0
 *
 * Descrição:
 *   Mini-preview ao vivo do hero do painel/login do afiliado, refletindo as
 *   configurações que o admin está editando na aba "Indicação". Reaplica
 *   as CSS vars do affiliate-shell num escopo isolado.
 * =============================================================================
 */
import { Sparkles, Link2, TrendingUp, Wallet, Check } from 'lucide-react';
import {
  buildBrandingCssVars,
  type AffiliateBrandingLogin,
  type AffiliateBrandingVisual,
} from '@/hooks/useAffiliateBranding';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface Props {
  branding: AffiliateBrandingVisual | null;
  login: AffiliateBrandingLogin | null;
}

const ICON_MAP = {
  link: Link2,
  chart: TrendingUp,
  wallet: Wallet,
  check: Check,
  sparkles: Sparkles,
} as const;

export function BrandingPreview({ branding, login }: Props) {
  const styleVars = buildBrandingCssVars(branding);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Pré-visualização
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Container isolado com escopo .affiliate-shell para herdar as vars */}
        <div className="affiliate-shell rounded-2xl overflow-hidden" style={styleVars}>
          <div
            className="aff-hero-surface relative p-6 min-h-[360px] flex flex-col justify-between"
          >
            {/* Marca topo */}
            <div className="relative z-10 flex items-center gap-2 text-[hsl(var(--aff-primary-foreground))]">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm overflow-hidden">
                {branding?.logo_url ? (
                  <img src={branding.logo_url} alt="" className="max-h-full max-w-full object-contain" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
              </div>
              <span className="text-[12px] font-semibold tracking-wide opacity-90">
                Painel do Afiliado
              </span>
            </div>

            {/* Conteúdo */}
            <div className="relative z-10 space-y-4 text-[hsl(var(--aff-primary-foreground))]">
              <div className="space-y-2">
                <h2 className="text-2xl font-bold tracking-tight leading-tight whitespace-pre-line">
                  {login?.title || 'Indique. Acompanhe. Receba.'}
                </h2>
                <p className="text-[13px] leading-relaxed opacity-90">
                  {login?.subtitle || 'Painel premium para afiliados.'}
                </p>
              </div>
              <ul className="space-y-2">
                {(login?.bullets ?? []).slice(0, 4).map((b, i) => {
                  const Icon = ICON_MAP[b.icon] ?? Sparkles;
                  return (
                    <li key={i} className="flex items-start gap-2 text-[12.5px] opacity-95">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-white/15">
                        <Icon className="h-3 w-3" />
                      </span>
                      <span>{b.text}</span>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Rodapé */}
            <div className="relative z-10 text-[10px] uppercase tracking-wide text-[hsl(var(--aff-primary-foreground))] opacity-75">
              {login?.footer_text || 'Programa de Indicação'}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
